import React, { createContext, useState, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTtsPlayer } from '../hooks/useTtsPlayer';

export const TtsContext = createContext(null);

export const useTts = () => {
  const context = useContext(TtsContext);
  if (!context) {
    throw new Error('useTts must be used within a TtsProvider');
  }
  return context;
};

export const TtsProvider = ({ children }) => {
  const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState(false);
  const [isWidgetVisible, setIsWidgetVisible] = useState(false);

  const {
    isPlaying,
    isPreparing,
    error,
    currentlyPlayingId,
    currentSource,
    startPlayback,
    stopPlayback,
    togglePlayPause,
    volume,
    setVolume,
  } = useTtsPlayer();

  const startTts = useCallback((items, source) => {
    if (!items || items.length === 0 || !source) {
      console.warn("startTts called with invalid arguments", { items, source });
      return;
    }
    console.log(`TtsContext: Received request to start TTS from source: ${source}`);
    setIsWidgetVisible(true); // Ensure widget is visible when playback starts
    startPlayback(items, source);
  }, [startPlayback]);

  const stopTts = useCallback(() => {
    console.log("TtsContext: Received request to stop TTS.");
    stopPlayback();
  }, [stopPlayback]);

  // Completely closes the widget, stops audio, and disables auto-play
  const closeAudioWidget = useCallback(() => {
    setIsWidgetVisible(false);
    setIsAutoPlayEnabled(false); // Disable auto-play when closing widget
    stopPlayback();
  }, [stopPlayback]);

  const openAudioWidget = useCallback(() => {
    setIsWidgetVisible(true);
    setIsAutoPlayEnabled(true); // Enable auto-play when opening widget via main button
  }, []);

  // Master toggle for the main header button
  const toggleAudioMode = useCallback(() => {
    if (isWidgetVisible) {
      closeAudioWidget();
    } else {
      openAudioWidget();
    }
  }, [isWidgetVisible, closeAudioWidget, openAudioWidget]);

  const toggleAutoPlay = useCallback(() => {
    setIsAutoPlayEnabled(prev => {
      const newAutoPlayState = !prev;
      console.log(`TtsContext: Toggling auto-play to ${newAutoPlayState}`);
      // If enabling auto-play via internal toggle, ensure widget is visible
      if (newAutoPlayState) {
        setIsWidgetVisible(true);
      }
      return newAutoPlayState;
    });
  }, []);

  const contextValue = useMemo(() => ({
    isPlaying,
    isPreparing,
    error,
    currentlyPlayingId,
    currentSource,
    startTts,
    stopTts,
    togglePlayPause,
    isAutoPlayEnabled,
    toggleAutoPlay,
    volume,
    setVolume,
    isWidgetVisible,
    toggleAudioMode, // New master toggle
    closeAudioWidget
  }), [
    isPlaying,
    isPreparing,
    error,
    currentlyPlayingId,
    currentSource,
    startTts,
    stopTts,
    togglePlayPause,
    isAutoPlayEnabled,
    toggleAutoPlay,
    volume,
    setVolume,
    isWidgetVisible,
    toggleAudioMode,
    closeAudioWidget
  ]);

  return (
    <TtsContext.Provider value={contextValue}>
      {children}
    </TtsContext.Provider>
  );
};

TtsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};